/**
 * Organization API Service
 * Il Diario di Bordo - SaaS
 * 
 * FASE 5: API per gestione Organization e ruolo Responsabile
 * FASE 3: Aggiunto sync2FAStatus per sincronizzazione 2FA
 * FASE 6: Aggiunta sezione Migrazione completa
 * FASE 6B: Aggiunta gestione Transfer Codes e Transfer Requests
 * 
 * Endpoint prefix: /api/organization e /api/onboarding/organization
 */

import api from './api'

// ============================================
// ONBOARDING API - Registrazione e Setup
// ============================================

export const organizationOnboardingApi = {
  /**
   * Registra nuova Organization + Responsabile
   * POST /api/onboarding/organization/register
   */
  register: (data) => api.post('/onboarding/organization/register', data),
  
  /**
   * Verifica email con token
   * POST /api/onboarding/organization/verify-email
   */
  verifyEmail: (token) => api.post('/onboarding/organization/verify-email', { token }),
  
  /**
   * Reinvia email verifica
   * POST /api/onboarding/organization/resend-verification
   */
  resendVerification: (email) => api.post('/onboarding/organization/resend-verification', { email }),
  
  /**
   * Ottieni stato setup e checklist
   * GET /api/onboarding/organization/status
   */
  getStatus: () => api.get('/onboarding/organization/status'),
  
  /**
   * Completa dati fiscali
   * POST /api/onboarding/organization/complete-fiscal
   */
  completeFiscal: (data) => api.post('/onboarding/organization/complete-fiscal', data),
  
  /**
   * Completa indirizzo sede legale
   * POST /api/onboarding/organization/complete-address
   */
  completeAddress: (data) => api.post('/onboarding/organization/complete-address', data),
  
  /**
   * Accetta documenti legali
   * POST /api/onboarding/organization/accept-contracts
   */
  acceptContracts: (documentIds) => api.post('/onboarding/organization/accept-contracts', { documentIds }),
  
  /**
   * Crea primo centro
   * POST /api/onboarding/organization/create-first-center
   */
  createFirstCenter: (data) => api.post('/onboarding/organization/create-first-center', data),
  
  /**
   * Lista documenti da accettare
   * GET /api/onboarding/organization/pending-contracts
   */
  getPendingContracts: () => api.get('/onboarding/organization/pending-contracts'),
  
  /**
   * Stato completo contratti (pending + signed + history)
   * GET /api/onboarding/organization/contracts-full
   */
  getContractsFull: () => api.get('/onboarding/organization/contracts-full'),
  
  /**
   * FASE 3: Sincronizza stato 2FA con Organization
   * POST /api/onboarding/organization/sync-2fa-status
   * 
   * Chiamato automaticamente dopo che il responsabile configura 2FA
   * per aggiornare setupChecklist.twoFactorAuth
   */
  sync2FAStatus: () => api.post('/onboarding/organization/sync-2fa-status'),
  
  /**
   * FASE 6B: Genera codice di ricezione durante onboarding
   * POST /api/onboarding/organization/generate-transfer-code
   * 
   * Usato quando l'Organization vuole ricevere un centro esistente
   * invece di crearne uno nuovo
   */
  generateTransferCode: () => api.post('/onboarding/organization/generate-transfer-code'),
  
  /**
   * FASE 6B: Completa setup senza creare centro (aspetta trasferimento)
   * POST /api/onboarding/organization/complete-awaiting-transfer
   */
  completeAwaitingTransfer: () => api.post('/onboarding/organization/complete-awaiting-transfer')
}

// ============================================
// ORGANIZATION API - Gestione Organization
// ============================================

export const organizationApi = {
  // === RESPONSABILE ===
  
  /**
   * Ottieni organization corrente
   * GET /api/organization/current
   */
  getCurrent: () => api.get('/organization/current'),
  
  /**
   * Aggiorna dati organization
   * PUT /api/organization/current
   */
  update: (data) => api.put('/organization/current', data),
  
  /**
   * Ottieni statistiche organization
   * GET /api/organization/stats
   */
  getStats: () => api.get('/organization/stats'),
  
  // === GESTIONE CENTRI ===
  
  /**
   * Lista centri dell'organization
   * GET /api/organization/centers
   */
  getCenters: (params) => api.get('/organization/centers', { params }),
  
  /**
   * Dettaglio centro
   * GET /api/organization/centers/:id
   */
  getCenter: (id) => api.get(`/organization/centers/${id}`),
  
  /**
   * Crea nuovo centro
   * POST /api/organization/centers
   */
  createCenter: (data) => api.post('/organization/centers', data),
  
  /**
   * Aggiorna centro
   * PUT /api/organization/centers/:id
   */
  updateCenter: (id, data) => api.put(`/organization/centers/${id}`, data),
  
  /**
   * Sospendi centro
   * POST /api/organization/centers/:id/suspend
   */
  suspendCenter: (id, reason) => api.post(`/organization/centers/${id}/suspend`, { reason }),
  
  /**
   * Riattiva centro
   * POST /api/organization/centers/:id/reactivate
   */
  reactivateCenter: (id) => api.post(`/organization/centers/${id}/reactivate`),
  
  /**
   * Richiedi cancellazione centro
   * POST /api/organization/centers/:id/request-deletion
   */
  requestCenterDeletion: (id, reason) => api.post(`/organization/centers/${id}/request-deletion`, { reason }),
  
  /**
   * Annulla cancellazione centro
   * POST /api/organization/centers/:id/cancel-deletion
   */
  cancelCenterDeletion: (id) => api.post(`/organization/centers/${id}/cancel-deletion`),

  // === SUBSCRIPTION CANCEL / REACTIVATE (per-centro, in-app) ===

  /**
   * Interrompe l'abbonamento Stripe del centro a fine periodo
   * POST /api/organization/centers/:centerId/subscription/cancel
   * @param {string} centerId
   * @param {string} [reason] - Motivo libero (max 500)
   */
  cancelCenterSubscription: (centerId, reason) =>
    api.post(`/organization/centers/${centerId}/subscription/cancel`, { reason }),

  /**
   * Riattiva l'abbonamento (annulla cancel_at_period_end)
   * POST /api/organization/centers/:centerId/subscription/reactivate
   */
  reactivateCenterSubscription: (centerId) =>
    api.post(`/organization/centers/${centerId}/subscription/reactivate`),

  // === COORDINATORI ===
  
  /**
   * Lista coordinatori di un centro
   * GET /api/organization/centers/:centerId/coordinators
   */
  getCenterCoordinators: (centerId) => api.get(`/organization/centers/${centerId}/coordinators`),
  
  /**
   * Invita coordinatore a un centro
   * POST /api/organization/centers/:centerId/coordinators/invite
   */
  inviteCoordinator: (centerId, data) => api.post(`/organization/centers/${centerId}/coordinators/invite`, data),
  
  /**
   * Rimuovi coordinatore da centro
   * DELETE /api/organization/centers/:centerId/coordinators/:userId
   */
  removeCoordinator: (centerId, userId) => api.delete(`/organization/centers/${centerId}/coordinators/${userId}`),
  
  /**
   * Ottieni info invito coordinatore (PIN incluso se pending)
   * GET /api/organization/centers/:centerId/coordinators/:userId/invite-info
   * 
   * Permette al Responsabile di rivedere il PIN finché l'invito non è stato accettato
   */
  getCoordinatorInviteInfo: (centerId, userId) => 
    api.get(`/organization/centers/${centerId}/coordinators/${userId}/invite-info`),
  
  /**
   * Rigenera e reinvia invito coordinatore
   * POST /api/organization/centers/:centerId/coordinators/:userId/resend-invite
   * 
   * Genera nuovo token e nuovo PIN, invalida il precedente.
   * @param {Object} data - Opzioni (requirePin: boolean)
   */
  resendCoordinatorInvite: (centerId, userId, data = {}) => 
    api.post(`/organization/centers/${centerId}/coordinators/${userId}/resend-invite`, data),
  
  // === DOCUMENTI LEGALI ===
  
  /**
   * Documenti firmati dall'organization
   * GET /api/organization/contracts
   */
  getContracts: () => api.get('/organization/contracts'),
  
  /**
   * Accetta nuovo documento
   * POST /api/organization/contracts/accept
   */
  acceptContract: (documentId, documentType, documentVersion) => api.post('/organization/contracts/accept', { 
    documentId, 
    documentType, 
    documentVersion 
  }),
  
  /**
   * Recupera il contenuto di un documento firmato (anche se archiviato)
   * GET /api/organization/contracts/:documentId/document
   * 
   * @param {string} documentId - ID del documento
   * @returns {Object} - { document, signature }
   */
  getSignedDocumentContent: (documentId) => api.get(`/organization/contracts/${documentId}/document`),
  
  // === DATI FISCALI ===
  
  /**
   * Ottieni dati fiscali
   * GET /api/organization/fiscal
   */
  getFiscal: () => api.get('/organization/fiscal'),
  
  /**
   * Aggiorna dati fiscali
   * PUT /api/organization/fiscal
   */
  updateFiscal: (data) => api.put('/organization/fiscal', data),
  
  // === BILLING ===
  
  /**
   * Riepilogo billing organization
   * GET /api/organization/billing
   */
  getBilling: () => api.get('/organization/billing'),
  
  /**
   * Fatture organization
   * GET /api/organization/invoices
   */
  getInvoices: (params) => api.get('/organization/invoices', { params }),
  
  // === FATTURAZIONE (Database Locale) ===
  
  /**
   * Lista fatture dal database locale
   * GET /api/invoices
   */
  getLocalInvoices: (params) => api.get('/invoices', { params }),
  
  /**
   * Download PDF fattura (generato on-demand)
   * GET /api/invoices/:invoiceId/download
   */
  downloadInvoicePdf: (invoiceId) => api.get(`/invoices/${invoiceId}/download`, { responseType: 'blob' }),
  
  /**
   * Reinvia email fattura con PDF
   * POST /api/invoices/:invoiceId/resend-email
   * Max 3 reinvii per fattura
   */
  resendInvoiceEmail: (invoiceId) => api.post(`/invoices/${invoiceId}/resend-email`),
  
  /**
   * Metodi di pagamento
   * GET /api/organization/payment-methods
   */
  getPaymentMethods: () => api.get('/organization/payment-methods'),
  
  /**
   * Piani disponibili per Organization
   * GET /api/organization/plans
   * 
   * Ritorna lista piani con prezzi in base a entityType
   * Include betaMode per controllo blur prezzi
   */
  getPlans: () => api.get('/organization/plans'),
  
  /**
   * Crea sessione Stripe Customer Portal
   * POST /api/organization/billing/portal
   * 
   * Reindirizza a Stripe per gestire abbonamento, fatture, metodi pagamento
   */
  createBillingPortalSession: () => api.post('/organization/billing/portal'),
  
  /**
   * Crea sessione Stripe Checkout per attivare piano su un centro
   * POST /api/organization/billing/checkout
   * 
   * @param {string} centerId - ID del centro
   * @param {string} plan - Piano (starter, professional, enterprise)
   * @param {string} interval - Intervallo (monthly, yearly)
   * @param {string} couponCode - Codice coupon opzionale
   */
  createCheckoutSession: (centerId, plan, interval, couponCode) => 
    api.post('/organization/billing/checkout', { centerId, plan, interval, couponCode }),
  
  /**
   * Upgrade/Downgrade piano di un centro con abbonamento attivo
   * POST /api/organization/billing/upgrade
   * 
   * Modifica subscription esistente su Stripe (prorata automatica).
   * Non apre Checkout — esegue il cambio piano direttamente.
   * 
   * @param {string} centerId - ID del centro
   * @param {string} plan - Nuovo piano (starter, professional, enterprise)
   * @param {string} interval - Intervallo (monthly, yearly)
   */
  upgradeCenterPlan: (centerId, plan, interval) => 
    api.post('/organization/billing/upgrade', { centerId, plan, interval }),
  
  // === CANCELLAZIONE (FASE 5) ===
  
  /**
   * Richiedi cancellazione organization
   * POST /api/organization/request-deletion
   * 
   * @param {string} reason - Motivo della cancellazione (min 10 caratteri)
   * @param {string} password - Password per conferma
   */
  requestDeletion: (reason, password) => api.post('/organization/request-deletion', { reason, password }),
  
  /**
   * Annulla cancellazione organization
   * POST /api/organization/cancel-deletion
   */
  cancelDeletion: () => api.post('/organization/cancel-deletion'),
  
  /**
   * Stato cancellazione
   * GET /api/organization/deletion-status
   */
  getDeletionStatus: () => api.get('/organization/deletion-status'),
  
  // === EXPORT DATI (FASE 5B) ===
  
  /**
   * Richiedi export dati GDPR
   * POST /api/organization/export-data
   */
  requestDataExport: () => api.post('/organization/export-data'),
  
  /**
   * Lista export effettuati
   * GET /api/organization/exports
   */
  getExports: () => api.get('/organization/exports'),
  
  /**
   * Download export (usa data-export API)
   * GET /api/data-export/download/:id
   */
  downloadExport: (id) => api.get(`/data-export/download/${id}`, { responseType: 'blob' }),
  
  // ============================================
  // FASE 6: MIGRAZIONE (Legacy - compatibilità)
  // ============================================
  
  // === SCENARIO A: CAMBIO COORDINATORE ===
  
  /**
   * Cambia coordinatore di un centro
   * POST /api/organization/centers/:id/change-coordinator
   * 
   * @param {string} centerId - ID del centro
   * @param {string} newCoordinatorId - ID del nuovo coordinatore
   * @param {string} reason - Motivo del cambio (opzionale)
   */
  changeCoordinator: (centerId, newCoordinatorId, reason) => 
    api.post(`/organization/centers/${centerId}/change-coordinator`, { 
      newCoordinatorId, 
      reason 
    }),
  
  // === SCENARIO B: TRASFERIMENTO RESPONSABILE ===
  
  /**
   * Trasferisci ruolo Responsabile a un altro utente
   * POST /api/organization/transfer-responsabile
   * 
   * @param {Object} data - Dati del nuovo responsabile
   * @param {string} data.newResponsabileEmail - Email
   * @param {string} data.newResponsabileNome - Nome
   * @param {string} data.newResponsabileCognome - Cognome
   * @param {string} data.password - Password attuale per conferma
   * @param {string} data.reason - Motivo (opzionale)
   */
  transferResponsabile: (data) => api.post('/organization/transfer-responsabile', data),
  
  // === SCENARIO C: TRASFERIMENTO ORGANIZATION (Legacy) ===
  
  /**
   * Avvia trasferimento centro a nuova organization
   * POST /api/organization/centers/:id/transfer-organization
   * 
   * @deprecated Usa sendTransferRequest con codice invece
   */
  transferToOrganization: (centerId, targetOrganizationId, consentDeadlineDays = 30, notes) =>
    api.post(`/organization/centers/${centerId}/transfer-organization`, {
      targetOrganizationId,
      consentDeadlineDays,
      notes
    }),
  
  /**
   * Ottieni stato migrazione centro
   * GET /api/organization/centers/:id/migration-status
   */
  getMigrationStatus: (centerId) => api.get(`/organization/centers/${centerId}/migration-status`),
  
  /**
   * Lista consensi per migrazione
   * GET /api/organization/centers/:id/migration-consents
   */
  getMigrationConsents: (centerId) => api.get(`/organization/centers/${centerId}/migration-consents`),
  
  /**
   * Aggiorna stato consenso (registra risposta genitore)
   * PUT /api/organization/migration-consents/:consentId
   */
  updateMigrationConsent: (consentId, data) => 
    api.put(`/organization/migration-consents/${consentId}`, data),
  
  /**
   * Aggiungi log comunicazione per un consenso
   * POST /api/organization/migration-consents/:consentId/log
   */
  addConsentCommunicationLog: (consentId, data) =>
    api.post(`/organization/migration-consents/${consentId}/log`, data),
  
  /**
   * Completa migrazione (dopo consensi)
   * POST /api/organization/centers/:id/complete-migration
   */
  completeMigration: (centerId) => 
    api.post(`/organization/centers/${centerId}/complete-migration`),
  
  /**
   * Annulla migrazione
   * POST /api/organization/centers/:id/cancel-migration
   */
  cancelMigration: (centerId) => 
    api.post(`/organization/centers/${centerId}/cancel-migration`),
  
  // ============================================
  // FASE 6B: TRANSFER CODES (Nuovo sistema)
  // ============================================
  
  // === CODICI DI RICEZIONE (Per Organization ricevente) ===
  
  /**
   * Genera un nuovo codice di ricezione
   * POST /api/organization/transfer-codes/generate
   * 
   * @param {number} validDays - Giorni di validità (default: 30)
   * @returns {Object} - { code, expiresAt, ... }
   */
  generateTransferCode: (validDays = 30) => 
    api.post('/organization/transfer-codes/generate', { validDays }),
  
  /**
   * Lista codici di ricezione attivi
   * GET /api/organization/transfer-codes
   */
  getTransferCodes: () => api.get('/organization/transfer-codes'),
  
  /**
   * Revoca un codice di ricezione
   * DELETE /api/organization/transfer-codes/:code
   */
  revokeTransferCode: (code) => api.delete(`/organization/transfer-codes/${code}`),
  
  // === VERIFICA CODICE (Per Organization cedente) ===
  
  /**
   * Verifica un codice di ricezione
   * POST /api/organization/transfer-codes/verify
   * 
   * @param {string} code - Codice da verificare
   * @returns {Object} - { valid, organization, error }
   */
  verifyTransferCode: (code) => 
    api.post('/organization/transfer-codes/verify', { code }),
  
  // === RICHIESTE DI TRASFERIMENTO ===
  
  /**
   * Invia richiesta di trasferimento centro
   * POST /api/organization/centers/:id/transfer-request
   * 
   * @param {string} centerId - ID del centro da trasferire
   * @param {Object} data - Dati della richiesta
   * @param {string} data.transferCode - Codice di ricezione
   * @param {boolean} data.gdprConfirmed - Conferma raccolta consensi GDPR
   * @param {string} data.notes - Note opzionali
   */
  sendTransferRequest: (centerId, data) => 
    api.post(`/organization/centers/${centerId}/transfer-request`, data),
  
  /**
   * Lista richieste di trasferimento in uscita (inviate da me)
   * GET /api/organization/transfer-requests/outgoing
   */
  getOutgoingTransferRequests: () => 
    api.get('/organization/transfer-requests/outgoing'),
  
  /**
   * Annulla una richiesta di trasferimento in uscita
   * POST /api/organization/transfer-requests/:id/cancel
   */
  cancelTransferRequest: (requestId, reason) => 
    api.post(`/organization/transfer-requests/${requestId}/cancel`, { reason }),
  
  // === RICHIESTE IN ARRIVO (Per Organization ricevente) ===
  
  /**
   * Lista richieste di trasferimento in arrivo
   * GET /api/organization/incoming-transfers
   */
  getIncomingTransfers: () => api.get('/organization/incoming-transfers'),
  
  /**
   * Approva una richiesta di trasferimento
   * POST /api/organization/incoming-transfers/:id/approve
   */
  approveTransfer: (requestId) => 
    api.post(`/organization/incoming-transfers/${requestId}/approve`),
  
  /**
   * Rifiuta una richiesta di trasferimento
   * POST /api/organization/incoming-transfers/:id/reject
   * 
   * @param {string} requestId - ID della richiesta
   * @param {string} reason - Motivo del rifiuto
   */
  rejectTransfer: (requestId, reason) => 
    api.post(`/organization/incoming-transfers/${requestId}/reject`, { reason }),
  
  // === HELPER MIGRAZIONE ===
  
  /**
   * Cerca organization per trasferimento
   * GET /api/organization/search
   * 
   * @param {string} query - Termine ricerca (nome, slug, P.IVA)
   * @deprecated Non usare - usa il sistema con codici invece
   */
  searchOrganizations: (query) => api.get('/organization/search', { params: { query } }),
  
  /**
   * Verifica se organization può ricevere un centro
   * GET /api/organization/:id/can-receive-center
   */
  canReceiveCenter: (organizationId) => 
    api.get(`/organization/${organizationId}/can-receive-center`)
}

// ============================================
// LEGAL DOCUMENTS API - Documenti Pubblici
// ============================================

export const legalDocumentsApi = {
  /**
   * Lista documenti attivi (pubblico)
   * GET /api/legal/documents
   */
  getDocuments: () => api.get('/legal/documents'),
  
  /**
   * Dettaglio documento (pubblico)
   * GET /api/legal/documents/:id
   */
  getDocument: (id) => api.get(`/legal/documents/${id}`),
  
  /**
   * Documento per tipo (pubblico)
   * GET /api/legal/documents/type/:type
   */
  getDocumentByType: (type) => api.get(`/legal/documents/type/${type}`),
  
  /**
   * Documenti da accettare (autenticato)
   * GET /api/legal/pending
   */
  getPending: () => api.get('/legal/pending'),
  
  /**
   * Accetta documento (autenticato)
   * POST /api/legal/acknowledge/:id
   */
  acknowledge: (id) => api.post(`/legal/acknowledge/${id}`),
  
  /**
   * Storico accettazioni utente (autenticato)
   * GET /api/legal/acknowledgments
   */
  getAcknowledgments: () => api.get('/legal/acknowledgments'),
  
  /**
   * Recupera il contenuto di un documento firmato (anche se archiviato)
   * Usato per visualizzare documenti firmati dall'utente
   * GET /api/legal/acknowledgments/:acknowledgmentId/document
   * 
   * @param {string} acknowledgmentId - ID dell'acknowledgment
   * @returns {Object} - { document, signature }
   */
  getSignedDocumentContent: (acknowledgmentId) => api.get(`/legal/acknowledgments/${acknowledgmentId}/document`)
}

export default {
  organizationOnboardingApi,
  organizationApi,
  legalDocumentsApi
}
